import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";

export default function ProductPage() {
  const { lookupKey } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [side, setSide] = useState("mock-front");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyName, setNotifyName] = useState("");
  const [notified, setNotified] = useState(false);
  const [earlyBird, setEarlyBird] = useState(false);
  const [position, setPosition] = useState(null);
  const [earlyLeft, setEarlyLeft] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const { addItem } = useCart();
  const wheelLock = useRef(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const autocartDone = useRef(false);

  useEffect(() => {
    setProduct(null);
    setSize(null);
    setSide("mock-front");
    api
      .get(`/products/${lookupKey}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
  }, [lookupKey]);

  useEffect(() => {
    if (!product || autocartDone.current) return;
    if (searchParams.get("autocart") !== "1") return;
    autocartDone.current = true;
    const s = searchParams.get("size");
    const em = searchParams.get("email");
    if (em) localStorage.setItem("paeon_cart_email", em);
    setSearchParams({}, { replace: true });
    if (s && (product.stock?.[s] ?? 0) > 0) {
      addItem(product, s);
      toast.success(`${product.name.split("/").pop()?.trim()} — size ${s} is in your cart.`);
    } else {
      toast.error("That size just sold out again — join the waitlist below.");
    }
  }, [product, searchParams, setSearchParams, addItem]);

  useEffect(() => {
    if (!product || product.status !== "coming_soon") return;
    api
      .get(`/restock-alerts/count/${product.lookup_key}`)
      .then(({ data }) => setEarlyLeft(data.early_bird_left))
      .catch(() => {});
  }, [product]);

  const add = () => {
    if (!size) {
      toast.error("Select a size first.");
      return;
    }
    if ((product.stock?.[size] ?? 0) <= 0) {
      toast.error(`Size ${size} is sold out.`);
      return;
    }
    addItem(product, size);
  };

  const notify = async () => {
    if (!size) {
      toast.error("Pick a size first.");
      return;
    }
    if (!notifyEmail.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    try {
      const { data } = await api.post("/restock-alerts", {
        email: notifyEmail,
        name: notifyName,
        lookup_key: product.lookup_key,
        size,
        origin_url: window.location.origin,
      });
      setEarlyBird(Boolean(data?.early_bird));
      setPosition(data?.position ?? null);
      setNotified(true);
      toast.success("You're on the list.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
    }
  };

  if (notFound) {
    return (
      <main data-testid="product-not-found" className="bg-white text-black min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="font-serif italic text-3xl">This piece has left the gallery.</p>
        <Link to="/shop" className="text-xs tracking-[0.3em] uppercase border-b border-black pb-1">
          Back to Shop
        </Link>
      </main>
    );
  }

  if (!product) {
    return <main data-testid="product-loading" className="bg-white min-h-screen" />;
  }

  const dark = product.color === "White";
  const comingSoon = product.status === "coming_soon";
  const stock = product.stock || {};
  const totalStock = Object.values(stock).reduce((a, b) => a + b, 0);
  const allSoldOut = totalStock <= 0;
  const sizeSoldOut = Boolean(size) && (stock[size] ?? 0) <= 0;

  const VIEWS = product.category === "hoodie"
    ? [
        { key: "mock-front", label: "Front", src: product.image },
        { key: "mock-back", label: "Back", src: product.image_back },
      ]
    : [
        { key: "mock-front", label: "Mockup Front", src: product.image },
        { key: "mock-back", label: "Mockup Back", src: product.image_back },
        { key: "real-front", label: "On Body Front", src: product.image_real },
        { key: "real-back", label: "On Body Back", src: product.image_real_back },
      ];

  const idx = Math.max(0, VIEWS.findIndex((v) => v.key === side));
  const step = (dir) => setSide(VIEWS[(idx + dir + VIEWS.length) % VIEWS.length].key);
  const onDragEnd = (_e, info) => {
    if (info.offset.x < -70) step(1);
    else if (info.offset.x > 70) step(-1);
  };
  const onWheel = (e) => {
    const now = Date.now();
    if (now - wheelLock.current < 350) return;
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 18) {
      wheelLock.current = now;
      step(e.deltaX > 0 ? 1 : -1);
    }
  };

  return (
    <main
      data-testid="product-page"
      className={`min-h-screen transition-colors duration-700 ${dark ? "bg-[#050505] text-white" : "bg-white text-black"}`}
    >
      <div className="pt-28 md:pt-36 px-6 md:px-12 pb-24 grid md:grid-cols-2 gap-10 md:gap-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div
            className={`relative overflow-hidden aspect-[4/5] cursor-grab active:cursor-grabbing ${dark ? "bg-[#0d0d0d]" : "bg-[#EBEBEB]"}`}
            onWheel={onWheel}
            data-testid="product-image-frame"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={side}
                src={VIEWS.find((v) => v.key === side)?.src || product.image}
                alt={`${product.name} — ${side}`}
                initial={{ opacity: 0, x: side.endsWith("front") ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="w-full h-full object-cover select-none"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.15}
                onDragEnd={onDragEnd}
                draggable={false}
                style={{ touchAction: "pan-y" }}
              />
            </AnimatePresence>
          </div>
          <p
            className={`mt-3 text-[9px] tracking-[0.3em] uppercase ${dark ? "text-white/30" : "text-black/30"}`}
            data-testid="swipe-hint"
          >
            Swipe or scroll sideways to change view
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                onClick={() => setSide(v.key)}
                data-testid={`view-${v.key}-button`}
                className={`px-5 py-2.5 text-[10px] tracking-[0.3em] uppercase border transition-colors duration-300 ${
                  side === v.key
                    ? dark
                      ? "bg-white text-black border-white"
                      : "bg-black text-white border-black"
                    : dark
                      ? "border-white/25 hover:border-white"
                      : "border-black/25 hover:border-black"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col justify-center max-w-lg"
        >
          <Link
            to="/shop"
            data-testid="product-back-link"
            className={`inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase transition-colors mb-10 w-fit ${
              dark ? "text-white/40 hover:text-white" : "text-black/40 hover:text-black"
            }`}
          >
            <ArrowLeft size={12} /> Shop
          </Link>
          <p className={`text-[10px] tracking-[0.35em] uppercase mb-4 ${dark ? "text-white/40" : "text-black/40"}`}>
            {product.tag} — {product.color}
          </p>
          <h1 data-testid="product-name" className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">
            {product.name}
          </h1>
          <p data-testid="product-price" className="text-xl mt-6 font-medium">
            {product.sale_percent ? (
              <>
                <span className={`line-through mr-3 font-normal ${dark ? "text-white/35" : "text-black/35"}`}>
                  ${product.price.toFixed(2)}
                </span>
                ${(product.price * (1 - product.sale_percent / 100)).toFixed(2)}
              </>
            ) : (
              `$${product.price.toFixed(2)}`
            )}
          </p>
          {comingSoon && (
            <p
              data-testid="product-coming-soon"
              className={`mt-2 text-[10px] tracking-[0.3em] uppercase ${dark ? "text-white/60" : "text-black/60"}`}
            >
              Coming Soon — Drop 002
            </p>
          )}
          {product.sale_percent && (
            <p
              data-testid="sale-note"
              className={`mt-3 text-[10px] tracking-[0.3em] uppercase font-semibold ${dark ? "text-white/70" : "text-black/70"}`}
            >
              {product.sale_percent}% off — ends October 25
            </p>
          )}
          <p className={`text-sm leading-relaxed mt-8 ${dark ? "text-white/60" : "text-black/60"}`}>
            {product.description}
          </p>

          <div className="mt-10">
            <div className="flex items-baseline justify-between mb-4">
              <p className={`text-[10px] tracking-[0.3em] uppercase ${dark ? "text-white/40" : "text-black/40"}`}>
                Size
              </p>
              {product.category !== "hoodie" && (
                <button
                  onClick={() => setShowMeasurements(true)}
                  data-testid="measurements-open-button"
                  className={`text-[10px] tracking-[0.3em] uppercase border-b pb-0.5 transition-colors duration-200 ${
                    dark
                      ? "border-white/40 text-white/70 hover:border-white hover:text-white"
                      : "border-black/40 text-black/70 hover:border-black hover:text-black"
                  }`}
                >
                  Measurements
                </button>
              )}
            </div>
            <div className="flex gap-3">
              {product.sizes.map((s) => {
                const gone = !comingSoon && (stock[s] ?? 0) <= 0;
                return (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-option-${s}`}
                    className={`w-12 h-12 border text-xs tracking-widest transition-colors duration-200 ${
                      gone
                        ? `line-through decoration-2 cursor-pointer ${
                            size === s
                              ? dark
                                ? "border-white"
                                : "border-black"
                              : `opacity-50 ${dark ? "border-white/40" : "border-black/30"}`
                          }`
                        : size === s
                          ? dark
                            ? "bg-white text-black border-white"
                            : "bg-black text-white border-black"
                          : dark
                            ? "border-white/25 hover:border-white"
                            : "border-black/25 hover:border-black"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            {!comingSoon && size && (stock[size] ?? 0) > 0 && (stock[size] ?? 0) < 10 && (
              <p
                data-testid="size-low-stock"
                className={`mt-4 text-[10px] tracking-[0.3em] uppercase font-semibold ${dark ? "text-white" : "text-black"}`}
              >
                Only {stock[size]} left — size {size}
              </p>
            )}
          </div>

          {comingSoon || sizeSoldOut || allSoldOut ? (
            <div className="mt-10" data-testid="restock-notify-block">
              <p className={`text-[10px] tracking-[0.3em] uppercase mb-4 ${dark ? "text-white/60" : "text-black/60"}`}>
                {comingSoon
                  ? `Drop 002 — coming soon. Join the waitlist; your size lands in your cart the moment it drops.${
                      earlyLeft !== null && earlyLeft > 0
                        ? ` First 5 per hoodie get $5 off — ${earlyLeft} of 5 spots left.`
                        : ""
                    }`
                  : `${allSoldOut && !size ? "Sold out" : `Size ${size} — sold out`}. Get notified when it returns.`}
              </p>
              {notified ? (
                <div>
                  <p
                    data-testid="restock-success"
                    className={`py-4 text-xs tracking-[0.3em] uppercase font-semibold border text-center ${
                      dark ? "border-white/40 text-white" : "border-black/40 text-black"
                    }`}
                  >
                    {earlyBird && position ? `You're in — #${position}, $5 off at drop` : "You're on the list"}
                  </p>
                  {earlyBird && (
                    <p className={`mt-3 text-[9px] tracking-[0.2em] uppercase text-center ${dark ? "text-white/40" : "text-black/40"}`}>
                      Use the same email at checkout — the $5 applies itself
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={notifyName}
                    onChange={(e) => setNotifyName(e.target.value)}
                    placeholder="NAME"
                    data-testid="restock-name-input"
                    className={`w-full px-4 py-4 bg-transparent text-xs tracking-[0.2em] uppercase outline-none border ${
                      dark ? "border-white/40 placeholder:text-white/30" : "border-black/40 placeholder:text-black/30"
                    }`}
                  />
                  <div className={`flex border ${dark ? "border-white/40" : "border-black/40"}`}>
                    <input
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder="EMAIL"
                      data-testid="restock-email-input"
                      className={`flex-1 px-4 py-4 bg-transparent text-xs tracking-[0.2em] uppercase outline-none ${
                        dark ? "placeholder:text-white/30" : "placeholder:text-black/30"
                      }`}
                    />
                    <button
                      onClick={notify}
                      data-testid="restock-notify-button"
                      className={`px-6 text-xs tracking-[0.3em] uppercase font-semibold transition-colors duration-300 ${
                        dark ? "bg-white text-black hover:bg-transparent hover:text-white" : "bg-black text-white hover:bg-transparent hover:text-black"
                      }`}
                    >
                      {comingSoon ? "Join Waitlist" : "Notify Me"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={add}
              data-testid="add-to-cart-button"
              className={`mt-10 py-4 text-xs tracking-[0.3em] uppercase font-semibold border transition-colors duration-300 ${
                dark
                  ? "bg-white text-black border-white hover:bg-transparent hover:text-white"
                  : "bg-black text-white border-black hover:bg-transparent hover:text-black"
              }`}
            >
              Add to Cart
            </button>
          )}

          <div className={`mt-12 space-y-4 border-t pt-8 text-xs leading-relaxed ${
            dark ? "border-white/10 text-white/50" : "border-black/10 text-black/50"
          }`}>
            {product.category === "hoodie" ? (
              <>
                <p>— 80% cotton, 20% polyester. 400gsm heavyweight fleece.</p>
                <p>— Boxy, neutral fit. Ribbed cuffs and hem.</p>
                <p>— Arch print across the chest, continuing down the sleeve.</p>
              </>
            ) : (
              <>
                <p>— 100% cotton, heavyweight. Pre-shrunk, garment-dyed.</p>
                <p>— DTG printed with archival-grade inks, front and back.</p>
                <p>— Boxy, structured fit. True to size.</p>
              </>
            )}
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showMeasurements && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowMeasurements(false)}
            data-testid="measurements-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-2xl border max-h-[85vh] overflow-y-auto ${
                dark ? "bg-[#0a0a0a] border-white/15 text-white" : "bg-white border-black/15 text-black"
              }`}
            >
              <div className={`flex items-center justify-between px-6 md:px-10 py-6 border-b ${dark ? "border-white/10" : "border-black/10"}`}>
                <div>
                  <p className="font-serif text-2xl md:text-3xl tracking-tight">Measurements</p>
                  <p className={`text-[9px] tracking-[0.3em] uppercase mt-2 ${dark ? "text-white/40" : "text-black/40"}`}>
                    7oz heavyweight tee — measured in inches
                  </p>
                </div>
                <button
                  onClick={() => setShowMeasurements(false)}
                  data-testid="measurements-close-button"
                  className={`text-[10px] tracking-[0.3em] uppercase border px-4 py-2.5 transition-colors duration-200 ${
                    dark ? "border-white/25 hover:border-white" : "border-black/25 hover:border-black"
                  }`}
                >
                  Close
                </button>
              </div>

              <div className="px-6 md:px-10 py-8">
                <div className={`grid grid-cols-4 gap-2 pb-4 border-b text-[9px] tracking-[0.25em] uppercase ${dark ? "border-white/15 text-white/40" : "border-black/15 text-black/40"}`}>
                  <span>Size</span>
                  <span className="text-right">Chest</span>
                  <span className="text-right">Length</span>
                  <span className="text-right">Sleeve</span>
                </div>
                {[
                  ["S", "20.75", "28", "20.125"],
                  ["M", "22.25", "29", "20.5"],
                  ["L", "23.75", "30", "21"],
                  ["XL", "25.25", "31", "21.375"],
                ].map(([s, chest, len, sleeve], i, arr) => (
                  <div
                    key={s}
                    data-testid={`measurements-row-${s}`}
                    className={`grid grid-cols-4 gap-2 py-5 text-sm ${i < arr.length - 1 ? (dark ? "border-b border-white/10" : "border-b border-black/10") : ""}`}
                  >
                    <span className="font-medium tracking-widest">{s}</span>
                    <span className="text-right tabular-nums">{chest}"</span>
                    <span className="text-right tabular-nums">{len}"</span>
                    <span className="text-right tabular-nums">{sleeve}"</span>
                  </div>
                ))}

                <div className={`mt-8 space-y-3 text-xs leading-relaxed ${dark ? "text-white/50" : "text-black/50"}`}>
                  <p>— Chest is measured flat, 1 inch below the armhole (pit to pit).</p>
                  <p>— Length is measured from the high point of the shoulder to the bottom hem.</p>
                  <p>— Sleeve is measured from the center back of the neck, over the shoulder, to the sleeve hem.</p>
                  <p>— Oversized, drop-shoulder fit. Boxy and structured.</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </main>
  );
}
